import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { PageLoading } from "@/components/ui/PageLoading";
import { RequireAdminSession } from "@/features/auth/components/RequireAdminSession";
import ForgotPasswordPage from "@/features/auth/pages/ForgotPasswordPage";
import LoginPage from "@/features/auth/pages/LoginPage";
import VerifyOtpPage from "@/features/auth/pages/VerifyOtpPage";

const CustomersPage = lazy(
  () => import("@/features/customers/pages/CustomersPage"),
);
const CustomerDetailPage = lazy(
  () => import("@/features/customers/pages/CustomerDetailPage"),
);
const PlaceholderAdminPage = lazy(
  () => import("@/features/admin/pages/PlaceholderAdminPage"),
);
const AccountManagersPage = lazy(
  () => import("@/features/account-managers/pages/AccountManagersPage"),
);
const AddAccountManagerPage = lazy(
  () => import("@/features/account-managers/pages/AddAccountManagerPage"),
);
const AccountManagerProfilePage = lazy(
  () => import("@/features/account-managers/pages/AccountManagerProfilePage"),
);
const NotFoundPage = lazy(
  () => import("@/features/not-found/pages/NotFoundPage"),
);
const NewCustomerWizardLayout = lazy(
  () => import("@/features/customers/new-customer/NewCustomerWizardLayout"),
);
const NewCustomerStep1Page = lazy(
  () => import("@/features/customers/new-customer/NewCustomerStep1Page"),
);
const NewCustomerStep2Page = lazy(
  () => import("@/features/customers/new-customer/NewCustomerStep2Page"),
);
const NewCustomerStep3Page = lazy(
  () => import("@/features/customers/new-customer/NewCustomerStep3Page"),
);
const NewCustomerSuccessLayout = lazy(
  () => import("@/features/customers/new-customer/NewCustomerSuccessLayout"),
);
const NewCustomerSuccessPage = lazy(
  () => import("@/features/customers/new-customer/NewCustomerSuccessPage"),
);

function SuspenseFallback({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <LoginPage />,
  },
  {
    path: "/login/verify-otp",
    element: <VerifyOtpPage />,
  },
  {
    path: "/login/reset-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/home",
    element: <Navigate to="/customers" replace />,
  },
  {
    element: <AdminLayout />,
    children: [
      {
        element: <RequireAdminSession />,
        children: [
          { path: "/customers", element: <CustomersPage /> },
          {
            path: "/customers/new",
            element: (
              <SuspenseFallback>
                <NewCustomerWizardLayout />
              </SuspenseFallback>
            ),
            children: [
              { index: true, element: <Navigate to="step-1" replace /> },
              { path: "step-1", element: <NewCustomerStep1Page /> },
              { path: "step-2", element: <NewCustomerStep2Page /> },
              { path: "step-3", element: <NewCustomerStep3Page /> },
            ],
          },
          {
            path: "/customers/:customerId/created",
            element: (
              <SuspenseFallback>
                <NewCustomerSuccessLayout />
              </SuspenseFallback>
            ),
            children: [{ index: true, element: <NewCustomerSuccessPage /> }],
          },
          {
            path: "/customers/new/success",
            element: <Navigate to="/customers" replace />,
          },
          {
            path: "/customers/:customerId",
            element: (
              <SuspenseFallback>
                <CustomerDetailPage />
              </SuspenseFallback>
            ),
          },
          { path: "/onboarding-queue", element: <PlaceholderAdminPage /> },
          {
            path: "/account-managers",
            element: (
              <SuspenseFallback>
                <AccountManagersPage />
              </SuspenseFallback>
            ),
          },
          {
            path: "/account-managers/new",
            element: (
              <SuspenseFallback>
                <AddAccountManagerPage />
              </SuspenseFallback>
            ),
          },
          {
            path: "/account-managers/:managerId",
            element: (
              <SuspenseFallback>
                <AccountManagerProfilePage />
              </SuspenseFallback>
            ),
          },
          { path: "/audit-log", element: <PlaceholderAdminPage /> },
          { path: "/settings", element: <PlaceholderAdminPage /> },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <SuspenseFallback>
        <NotFoundPage />
      </SuspenseFallback>
    ),
  },
]);
